import type {
  Included,
  Projection,
  ProjectionsAndScoresAPIResponse
} from '@library/types/projection';
import { isTimeAfter, isTimeWithinInterval } from '@library/utils/dateTime';
import { DateTime } from 'luxon';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { PlayerModalProjectionsRequest } from '@library/types/playerModal';
import { getPlayerValues } from '@library/utils/players';
import { getPromotionValues } from '@library/utils';
import playerImagePlaceholder from '@library/assets/images/icons/player-placeholder.svg';

export const getPlayerModalProjections = (
  projections: ProjectionsAndScoresAPIResponse
): Array<GamePlayerProjectionTrading> => {
  const playerModalProjections: Array<GamePlayerProjectionTrading> =
    projections.data.map((projection) => {
      const {
        id: projectionId,
        attributes: {
          auto_trade: autoTrade,
          board_time: boardTime,
          data_source: dataSource,
          description,
          game_id: gameId,
          has_suggested_lines: hasSuggestedLines,
          in_game: inGame,
          is_community_play: isCommunityPlay,
          line_score: projectionValue,
          max_probability_of_more: maxProbabilityOfMore,
          min_probability_of_more: minProbabilityOfMore,
          odds_type: oddsType,
          probability_of_more: probabilityOfMore,
          rank,
          start_time: startTime,
          display_time: displayTime,
          stat_type: statType,
          status,
          target_probability_of_more: targetProbabilityOfMore
        },
        relationships: {
          duration: { data: duration },
          stat_type: { data: statTypeData },
          league: { data: league }
        }
      } = projection;

      const {
        foreignId,
        market,
        playerId,
        playerImage,
        playerName,
        position,
        team,
        teamName
      } = getPlayerValues({
        projection,
        included: projections.included
      });

      const { discountPercentage, flashSaleLineScore, isPromo, optIn } =
        getPromotionValues({ included: projections.included, projection });

      const data: GamePlayerProjectionTrading = {
        autoTrade,
        boardTime,
        dataSource,
        description,
        discountPercentage,
        durationId: duration?.id,
        flashSaleLineScore,
        foreignId,
        gameId: gameId ?? '',
        hasSuggestedLines,
        leagueId: league?.id ?? undefined,
        inGame,
        isCommunityPlay,
        isPromo,
        market,
        maxProbabilityOfMore,
        minProbabilityOfMore,
        oddsType,
        optIn,
        playerId,
        playerImage: playerImage ?? playerImagePlaceholder,
        playerName,
        position,
        probabilityOfMore,
        projectionId,
        projectionValue: Number(projectionValue),
        rank,
        startTime,
        displayTime,
        statType,
        statTypeId: statTypeData?.id,
        status,
        targetProbabilityOfMore,
        team,
        teamName
      };

      return data;
    });

  return playerModalProjections ?? [];
};

export const isRelevantWSMessage = ({
  arg,
  projectionData
}: {
  arg: PlayerModalProjectionsRequest;
  projectionData: { data: Projection<string>; included: Included<string>[] };
}): boolean => {
  const { data, included } = projectionData;

  const {
    attributes: { start_time: startTime, game_id: gameId },
    relationships: {
      duration: { data: duration },
      league: { data: league },
      new_player: { data: newPlayer }
    }
  } = data;

  const fromDate = DateTime.fromISO(arg.fromDate);
  const toDate = arg.toDate && DateTime.fromISO(arg.toDate);
  const messageProjectionDate = DateTime.fromISO(startTime);

  if (
    (toDate
      ? !isTimeWithinInterval({
          time: messageProjectionDate,
          start: fromDate,
          end: toDate
        })
      : isTimeAfter({
          dateValue: fromDate,
          dateToCompareAgainst: messageProjectionDate
        })) ||
    (arg.leagueId &&
      arg.leagueId.length > 0 &&
      !arg.leagueId.includes(league?.id ?? '')) ||
    (arg.durationId &&
      arg.durationId.length > 0 &&
      !arg.durationId.includes(duration?.id ?? '')) ||
    (arg.gameId && arg.gameId !== gameId) ||
    (arg.playerId && newPlayer?.id !== arg.playerId)
  ) {
    return false;
  }

  const player = included.find(
    (projectionRelation) => projectionRelation.type === 'new_player'
  );

  if (!player) {
    return false;
  }

  return true;
};
